import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { Workspace, WorkspaceMember, User, WebSocketEvent, CreateWorkspaceRequest, UpdateWorkspaceRequest } from '@sahaj/types';
import { api, ws } from '../lib/api';
import { topics } from '@sahaj/websocket';
import { useAuth } from './AuthContext';

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  members: WorkspaceMember[];
  isLoading: boolean;
  selectWorkspace: (workspaceId: string) => void;
  createWorkspace: (name: string, slug: string, description?: string, avatarUrl?: string) => Promise<Workspace>;
  updateWorkspace: (name: string, description?: string, avatarUrl?: string) => Promise<Workspace>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  uploadWorkspaceIcon: (file: File | Blob) => Promise<Workspace>;
  removeWorkspaceIcon: () => Promise<Workspace>;
  refreshWorkspaces: () => Promise<void>;
  refreshMembers: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      return;
    }
    try {
      setIsLoading(true);
      const list = await api.workspaces.list();
      setWorkspaces(list);
      if (list.length > 0) {
        setCurrentWorkspace(prev => {
          if (prev && list.some(w => w.id === prev.id)) {
            const found = list.find(w => w.id === prev.id);
            return found ?? list[0] ?? null;
          }
          return list[0] ?? null;
        });
      } else {
        setCurrentWorkspace(null);
      }
    } catch (err) {
      console.error('Failed to load workspaces', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const fetchMembers = useCallback(async () => {
    if (!currentWorkspace) {
      setMembers([]);
      return;
    }
    try {
      const list = await api.workspaces.listMembers(currentWorkspace.id);
      setMembers(list);
    } catch (err) {
      console.error('Failed to load workspace members', err);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Subscribe to real-time workspace updates and member identity updates
  useEffect(() => {
    if (!currentWorkspace) return;

    const unsub = ws.subscribe(topics.workspace(currentWorkspace.id), (event: WebSocketEvent) => {
      if (event.eventType === 'WORKSPACE_UPDATED' && event.payload) {
        const updatedWs = event.payload as Workspace;
        if (updatedWs.id === currentWorkspace.id) {
          setCurrentWorkspace(updatedWs);
        }
        setWorkspaces(prev => prev.map(w => w.id === updatedWs.id ? updatedWs : w));
      } else if (event.eventType === 'WORKSPACE_DELETED') {
        const payload = event.payload as any;
        const deletedId = payload?.workspaceId || payload?.id || currentWorkspace.id;
        setWorkspaces(prev => {
          const nextList = prev.filter(w => w.id !== deletedId);
          if (currentWorkspace?.id === deletedId) {
            setCurrentWorkspace(nextList[0] ?? null);
          }
          return nextList;
        });
      } else if (event.eventType === 'USER_UPDATED' && event.payload) {
        const updatedUser = event.payload as User;
        setMembers(prev =>
          prev.map(m => {
            if (m.user.id === updatedUser.id) {
              return {
                ...m,
                user: {
                  ...m.user,
                  displayName: updatedUser.displayName,
                  ...(updatedUser.avatarUrl ? { avatarUrl: updatedUser.avatarUrl } : {}),
                },
              };
            }
            return m;
          })
        );
      }
    });

    return () => {
      unsub();
    };
  }, [currentWorkspace?.id]);

  const selectWorkspace = (workspaceId: string) => {
    const found = workspaces.find(w => w.id === workspaceId);
    if (found) setCurrentWorkspace(found);
  };

  const createWorkspace = async (name: string, slug: string, description?: string, avatarUrl?: string) => {
    const payload: CreateWorkspaceRequest = {
      name,
      slug,
      ...(description ? { description } : {}),
      ...(avatarUrl ? { avatarUrl } : {}),
    };

    const created = await api.workspaces.create(payload);
    await fetchWorkspaces();
    setCurrentWorkspace(created);
    return created;
  };

  const updateWorkspace = async (name: string, description?: string, avatarUrl?: string) => {
    if (!currentWorkspace) throw new Error('No active workspace');
    const payload: UpdateWorkspaceRequest = {
      name,
      ...(description !== undefined ? { description } : {}),
      ...(avatarUrl !== undefined ? { avatarUrl } : {}),
    };
    const updated = await api.workspaces.update(currentWorkspace.id, payload);
    setCurrentWorkspace(updated);
    setWorkspaces(prev => prev.map(w => w.id === updated.id ? updated : w));
    return updated;
  };

  const deleteWorkspace = async (workspaceId: string) => {
    await api.workspaces.delete(workspaceId);
    setWorkspaces(prev => {
      const remaining = prev.filter(w => w.id !== workspaceId);
      if (currentWorkspace?.id === workspaceId) {
        setCurrentWorkspace(remaining[0] ?? null);
      }
      return remaining;
    });
    await fetchWorkspaces();
  };

  const uploadWorkspaceIcon = async (file: File | Blob) => {
    if (!currentWorkspace) throw new Error('No active workspace');
    const updated = await api.workspaces.uploadIcon(currentWorkspace.id, file);
    setCurrentWorkspace(updated);
    setWorkspaces(prev => prev.map(w => w.id === updated.id ? updated : w));
    return updated;
  };

  const removeWorkspaceIcon = async () => {
    if (!currentWorkspace) throw new Error('No active workspace');
    const updated = await api.workspaces.deleteIcon(currentWorkspace.id);
    setCurrentWorkspace(updated);
    setWorkspaces(prev => prev.map(w => w.id === updated.id ? updated : w));
    return updated;
  };

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        members,
        isLoading,
        selectWorkspace,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        uploadWorkspaceIcon,
        removeWorkspaceIcon,
        refreshWorkspaces: fetchWorkspaces,
        refreshMembers: fetchMembers
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return context;
};
